import { makeAutoObservable } from "mobx";

import { getPartiesMap } from "@/shared/api/endpoints";
import { PartiesMapDTO } from "@/shared/api/endpoints/parties/types";
import { SelectorValue, toSelectorLabelValue } from "@/shared/utils/forms";
import { logError } from "@/shared/utils/logger";

export interface Party {
	partyUUID: string;
	partyName: string;
}

export class PartiesStore {
	private _partiesMap: PartiesMapDTO = {};

	private _partiesList: Party[] = [];

	private _partiesOptions: SelectorValue<string>[] = [];

	private _isLoadedParties = false;

	constructor() {
		makeAutoObservable(this);
	}

	get partiesMap() {
		return this._partiesMap;
	}

	get partiesList() {
		return this._partiesList;
	}

	get partiesOptions() {
		return this._partiesOptions;
	}

	get isLoadedParties() {
		return this._isLoadedParties;
	}

	getPartyName = (partyUUID: string) => this._partiesMap[partyUUID] || null;

	loadData = async () => {
		if (this._isLoadedParties) return;

		await this._fetchPartiesMap();
	};

	updateData = async () => {
		await this._fetchPartiesMap();
	};

	private _fetchPartiesMap = async () => {
		try {
			const { isError, data } = await getPartiesMap();

			if (!isError) {
				this._setPartiesMap(data);
				this._setPartiesList(data);
				this._setPartiesOptions(data);
			}
		} catch (error) {
			logError(error);
		} finally {
			this._setIsLoadedParties(true);
		}
	};

	private _setPartiesMap = (partiesMap: PartiesMapDTO) => {
		this._partiesMap = partiesMap;
	};

	private _setPartiesList = (partiesMap: PartiesMapDTO) => {
		this._partiesList = Object.entries(partiesMap).map(([partyUUID, partyName]) => ({
			partyUUID,
			partyName,
		}));
	};

	private _setPartiesOptions = (partiesMap: PartiesMapDTO) => {
		this._partiesOptions = Object.entries(partiesMap).map(([partyUUID, partyName]) =>
			toSelectorLabelValue(partyName, partyUUID)
		);
	};

	private _setIsLoadedParties = (isLoaded: boolean) => {
		this._isLoadedParties = isLoaded;
	};
}
